import { isSeoPathIndexable } from "@/config/seoRoutes";
import { DEFAULT_LANGUAGE, getRouteKeyFromPath, localizedRoutes, stripLanguageFromPath } from "@/lib/i18nRoutes";

const DISALLOWED_PATH_PATTERNS = [
  /^\/admin(?:\/|$)/,
  /^\/billing(?:\/|$)/,
  /^\/settings(?:\/|$)/,
  /^\/profile(?:\/|$)/,
  /^\/onboarding(?:\/|$)/,
  /^\/logbook(?:\/|$)/,
  /^\/boats(?:\/|$)/,
  /^\/trips(?:\/|$)/,
  /^\/dashboard(?:\/|$)/,
  /^\/metrics(?:\/|$)/,
  /^\/offline(?:\/|$)/,
  /^\/premium-operations(?:\/|$)/,
  /^\/404(?:\/|$)/,
];

const DISALLOWED_LOCALIZED_SEGMENTS = new Set([
  "profile",
  "dashboard",
  "billing",
  "settings",
  "onboarding",
  "logbook",
  "boats",
  "trips",
  "metrics",
  "offline",
  "premium",
  "chart-plotter",
  "advanced-voyage-planner",
  "ai-skipper",
  "boat-document-vault",
  "available-soon",
]);

const PROGRAMMATIC_SEO_RE = /^\/(?:[a-z]{2}\/)?(?:sailing-regulations|marina-comparisons|weather-guides|sailing-checklists|sailing-locations)\/[^/?#]+/;
const NEAR_ME_SEO_RE = /^\/(?:[a-z]{2}\/)?(?:marinas-near-me|anchorages-near-me|fuel-near-me|marine-weather-near-me|boat-services-near-me)(\/|$)/;

const AUTO_AD_ROUTE_KEYS = new Set([
  "home",
  "weather",
  "marinas",
  "learningCenter",
  "tools",
  "wire",
  "dailyBriefing",
  "destinations",
  "routes",
  "regulations",
  "glossary",
  "safety",
]);

const AUTO_AD_CONTENT_PATTERNS = [
  /^\/$/,
  /^\/weather(?:\/|$)/,
  /^\/marinas(?:\/|$)/,
  /^\/learning-center(?:\/|$)/,
  /^\/sailing-calculators(?:\/|$)/,
  /^\/wire(?:\/|$)/,
  /^\/daily-briefing(?:\/|$)/,
  /^\/destinations(?:\/|$)/,
  /^\/routes(?:\/|$)/,
  /^\/regulations(?:\/|$)/,
  /^\/glossary(?:\/|$)/,
  /^\/safety(?:\/|$)/,
];

function isCanonicalSearch(search = "") {
  const params = new URLSearchParams(search || "");
  if ([...params.keys()].length === 0) return true;
  if ([...params.keys()].length === 1 && /^\d+$/.test(params.get("page") || "")) return true;
  return false;
}

function registryPathFor(pathname = "/") {
  const cleanPath = stripLanguageFromPath(pathname || "/") || "/";
  const routeKey = getRouteKeyFromPath(pathname);
  return routeKey && localizedRoutes[routeKey]?.[DEFAULT_LANGUAGE] != null
    ? `/${localizedRoutes[routeKey][DEFAULT_LANGUAGE]}`.replace(/\/$/, "") || "/"
    : cleanPath;
}

export function isAdsenseAllowedPath(pathname = "/", search = "") {
  const adsenseReviewMode = true;
  if (adsenseReviewMode) return false;
  const adsenseApproved = import.meta.env.VITE_ADSENSE_APPROVED === "true";
  if (!adsenseApproved) return false;
  if (!isCanonicalSearch(search)) return false;
  if (PROGRAMMATIC_SEO_RE.test(pathname) || NEAR_ME_SEO_RE.test(pathname)) return false;
  if (DISALLOWED_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) return false;

  const segments = pathname.split("/").filter(Boolean);
  const firstContentSegment = segments.length > 1 ? segments[1] : segments[0];

  if (DISALLOWED_LOCALIZED_SEGMENTS.has(firstContentSegment)) return false;

  return isSeoPathIndexable(registryPathFor(pathname));
}

export function isAdsenseAutoAdsPath(pathname = "/", search = "") {
  if (!isAdsenseAllowedPath(pathname, search)) return false;

  const registryPath = registryPathFor(pathname);
  const routeKey = getRouteKeyFromPath(pathname);
  if (routeKey && AUTO_AD_ROUTE_KEYS.has(routeKey)) return true;

  return AUTO_AD_CONTENT_PATTERNS.some((pattern) => pattern.test(registryPath));
}

export function getAdContainerClass(extraClassName = "") {
  return [
    "mx-auto my-10 w-full max-w-full overflow-hidden rounded-2xl border border-border/60 bg-card/40 p-3",
    "sm:my-12 sm:p-4",
    extraClassName,
  ].filter(Boolean).join(" ");
}

export const AD_LABEL = "Advertisement";